"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { io } from "socket.io-client"
import { getCookie } from "../utilities/cookieHelper" // Import cookie helper
// import chatIcon from "../assets/chatwidget.png"; // Removed as chatIconUrl from config is now used

import {
  Box,
  Button,
  IconButton,
  TextField,
  Avatar,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  FormControl,
  FormLabel,
  Fab,
  GlobalStyles,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import MinimizeIcon from "@mui/icons-material/Minimize" // Or another appropriate icon like Remove
import HomeIcon from "@mui/icons-material/Home"
import ChatIcon from "@mui/icons-material/Chat"
import SendIcon from "@mui/icons-material/Send"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import EditIcon from "@mui/icons-material/Edit" // Example for "Effortless Typing"
import SettingsIcon from "@mui/icons-material/Settings" // Example for "Customizable Chat"
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions"
import { X, MessageCircle, Send, ChevronDown, ChevronUp, Minimize2, Maximize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:9100"
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:9100"

const AGENT_AVATAR = "https://ui-avatars.com/api/?name=Agent&background=007bff&color=fff&size=64"
const USER_AVATAR = "https://ui-avatars.com/api/?name=You&background=888&color=fff&size=64"

const agentId = "agent-1"

// Utility functions to detect browser and OS information
const detectBrowser = () => {
  const userAgent = navigator.userAgent;
  let browser = "Unknown";
  
  if (userAgent.includes("Firefox")) {
    browser = "Firefox";
  } else if (userAgent.includes("Chrome")) {
    browser = "Chrome";
  } else if (userAgent.includes("Safari")) {
    browser = "Safari";
  } else if (userAgent.includes("Edge")) {
    browser = "Edge";
  } else if (userAgent.includes("Opera")) {
    browser = "Opera";
  } else if (userAgent.includes("MSIE") || userAgent.includes("Trident")) {
    browser = "Internet Explorer";
  }
  
  return browser;
};

const detectOS = () => {
  const userAgent = navigator.userAgent;
  let os = "Unknown";
  
  if (userAgent.includes("Windows")) {
    os = "Windows";
  } else if (userAgent.includes("Mac")) {
    os = "macOS";
  } else if (userAgent.includes("Linux")) {
    os = "Linux";
  } else if (userAgent.includes("Android")) {
    os = "Android";
  } else if (userAgent.includes("iOS")) {
    os = "iOS";
  }
  
  return os;
};

const getIPAddressAndLocation = async () => {
  try {
    // Try ipinfo.io first (reliable and provides comprehensive data)
    const geoRes = await fetch('https://ipinfo.io/json');
    if (!geoRes.ok) throw new Error(`ipinfo.io status: ${geoRes.status}`);
    const geoData = await geoRes.json();
    console.log("[ChatWidget Location DEBUG] ipinfo.io response:", geoData);

    const [lat, lon] = geoData.loc ? geoData.loc.split(',') : [null, null];

    return {
      ip: geoData.ip || null,
      country: geoData.country || null,
      region: geoData.region || null,
      city: geoData.city || null,
      lat: lat ? parseFloat(lat) : null,
      lon: lon ? parseFloat(lon) : null,
      full: geoData // for debugging
    };
  } catch (error) {
    console.error('Error fetching IP/location from ipinfo.io:', error);

    // Fallback to db-ip.com
    try {
      const geoRes = await fetch('https://api.db-ip.com/v2/free/self');
      if (!geoRes.ok) throw new Error(`db-ip.com status: ${geoRes.status}`);
      const geoData = await geoRes.json();
      console.log("[ChatWidget Location DEBUG] db-ip.com response:", geoData);

      return {
        ip: geoData.ipAddress || null,
        country: geoData.countryName || null,
        region: geoData.stateProv || null,
        city: geoData.city || null,
        lat: geoData.latitude || null,
        lon: geoData.longitude || null,
        full: geoData // for debugging
      };
    } catch (dbIpError) {
      console.error('Error fetching IP/location from db-ip.com:', dbIpError);

      // Final fallback to ipify.org for just IP address
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        if (!ipRes.ok) throw new Error(`ipify.org status: ${ipRes.status}`);
        const ipData = await ipRes.json();
        console.log("[ChatWidget Location DEBUG] Fallback IP response:", ipData);

        return {
          ip: ipData.ip || null,
          country: null,
          region: null,
          city: null,
          lat: null,
          lon: null,
          full: { ip: ipData.ip, note: 'IP only - location failed' }
        };
      } catch (fallbackError) {
        console.error('Error fetching IP from fallback:', fallbackError);
        return {
          ip: null,
          country: null,
          region: null,
          city: null,
          lat: null,
          lon: null,
          full: { error: 'All location services failed' }
        };
      }
    }
  }
};

function formatTime(date) {
  if (!date) return ""
  const d = typeof date === "string" ? new Date(date) : date
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })
}

// Simple Typing Indicator for the widget
const WidgetTypingIndicator = ({ name }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "8px 12px",
      margin: "4px 0",
      fontStyle: "italic",
      color: "#555",
    }}
    className="widget-typing-indicator"
  >
    <Typography variant="body2">{name || "Support"} is typing...</Typography>
  </Box>
)

// Placeholder SplashScreen component
const SplashScreen = ({ config }) => (
  <div
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "#fff",
      display: "flex",
      flexDirection: "column",
      zIndex: 1000,
      animation: "fadeOut 0.5s ease-out forwards",
      animationDelay: "3.5s",
      borderRadius: "18px",
      overflow: "hidden",
      boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
    }}
  >
    {/* Header */}
    <div
      style={{
        background: config.headerColor,
        padding: "12px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        color: "white",
        fontWeight: "bold",
        fontSize: "16px",
      }}
    >
      <div>CLIKKLE Support</div>
      <div style={{ display: "flex", gap: "12px" }}>
        <span style={{ cursor: "pointer" }}>▼</span>
        <span style={{ cursor: "pointer" }}>✕</span>
      </div>
    </div>

    {/* Content */}
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background animation dots */}
      <div
        className="bg-animation"
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 0 }}
      ></div>

      {/* Logo */}
      <div
        style={{
          width: "80px",
          height: "80px",
          borderRadius: "50%",
          // background: config.headerColor,
          background: "white",
          border: "1px solid #007bff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
          position: "relative",
          animation: "logoAppear 0.5s ease-out forwards",
          zIndex: 1,
        }}
      >
        {/* <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 11.5C21 16.75 16.75 21 11.5 21C6.25 21 2 16.75 2 11.5C2 6.25 6.25 2 11.5 2C16.75 2 21 6.25 21 11.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 22L20 20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15 11.5C15 13.9853 12.9853 16 10.5 16C8.01472 16 6 13.9853 6 11.5C6 9.01472 8.01472 7 10.5 7C12.9853 7 15 9.01472 15 11.5Z" fill="white"/>
        </svg> */}

        <img
          src="https://cdn.clikkle.com/images/crm/2025/clikkle%20crm.png"
          alt="Chat Logo"
          style={{ width: "38px", height: "38px", borderRadius: "50%" }}
        />
        {/* Info bubble */}
        <div
          style={{
            position: "absolute",
            top: "-10px",
            right: "-20px",
            width: "30px",
            height: "30px",
            borderRadius: "50%",
            background: "#4f8cff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "bold",
            animation: "bubblePop 0.5s ease-out forwards",
            animationDelay: "0.5s",
            opacity: 0,
            transform: "scale(0)",
          }}
        >
          !
        </div>
      </div>

      {/* Brand name */}
      <div
        style={{
          fontSize: "24px",
          fontWeight: "bold",
          color: config.headerColor,
          marginBottom: "10px",
          animation: "fadeInUp 0.5s ease-out forwards",
          animationDelay: "0.3s",
          opacity: 0,
          transform: "translateY(20px)",
          zIndex: 1,
        }}
      >
        CLIKKLE
      </div>

      {/* Welcome text */}
      <div
        style={{
          fontSize: "18px",
          color: "#555",
          marginBottom: "5px",
          animation: "fadeInUp 0.5s ease-out forwards",
          animationDelay: "0.5s",
          opacity: 0,
          transform: "translateY(20px)",
          zIndex: 1,
        }}
      >
        Welcome to Clikkle Chat
      </div>

      {/* Subtitle */}
      <div
        style={{
          fontSize: "14px",
          color: "#777",
          marginBottom: "30px",
          animation: "fadeInUp 0.5s ease-out forwards",
          animationDelay: "0.7s",
          opacity: 0,
          transform: "translateY(20px)",
          zIndex: 1,
        }}
      >
        Your intelligent assistant
      </div>

      {/* Progress bar */}
      <div
        style={{
          width: "80%",
          height: "4px",
          background: "#e0e0e0",
          borderRadius: "2px",
          overflow: "hidden",
          marginBottom: "20px",
          animation: "fadeInUp 0.5s ease-out forwards",
          animationDelay: "0.9s",
          opacity: 0,
          transform: "translateY(20px)",
          zIndex: 1,
        }}
      >
        <div
          style={{
            height: "100%",
            background: config.headerColor,
            animation: "progressBar 2.5s ease-in-out forwards",
            width: "0%",
          }}
        ></div>
      </div>

      {/* Loading text */}
      <div
        style={{
          fontSize: "14px",
          color: "#aaa",
          animation: "fadeInUp 0.5s ease-out forwards, textFade 2s ease-in-out infinite",
          animationDelay: "1.1s",
          opacity: 0,
          transform: "translateY(20px)",
          zIndex: 1,
        }}
      >
        Loading your personalized experience...
      </div>
    </div>

    <style>{`
      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; visibility: hidden; }
      }
      
      @keyframes logoAppear {
        from { transform: scale(0.5); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      
      @keyframes bubblePop {
        from { transform: scale(0); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      
      @keyframes fadeInUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      @keyframes progressBar {
        0% { width: 0%; }
        20% { width: 20%; }
        50% { width: 50%; }
        80% { width: 80%; }
        100% { width: 100%; }
      }
      
      @keyframes textFade {
        0% { opacity: 0.7; }
        50% { opacity: 1; }
        100% { opacity: 0.7; }
      }
      
      .bg-animation {
        background: linear-gradient(45deg, rgba(240, 245, 255, 0.5) 25%, transparent 25%, transparent 50%, rgba(240, 245, 255, 0.5) 50%, rgba(240, 245, 255, 0.5) 75%, transparent 75%, transparent);
        background-size: 20px 20px;
        animation: bgMove 15s linear infinite;
      }
      
      @keyframes bgMove {
        0% { background-position: 0 0; }
        100% { background-position: 40px 40px; }
      }
    `}</style>
  </div>
)

const AdvancedChatWidget = (props) => {
  console.log("AdvancedChatWidget mounted. Initial props.config:", JSON.stringify(props.config)) // Log incoming props

  // Update defaultConfig to ensure all config options are properly merged
  const defaultConfig = {
    widgetId: "",
    position: "bottom-right",
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
    headerColor: "#007bff",
    headerTextColor: "#ffffff",
    widgetBackgroundColor: "#fff",
    userMessageColor: "#007bff",
    userMessageTextColor: "#000000",
    agentMessageColor: "#e9f0fb",
    agentMessageTextColor: "#222",
    greetingMessage: "Hello! How can we help you today?",
    requirePreChatForm: false,
    preChatFormFields: {
      name: { label: "Name", required: true, placeholder: "Enter your name" },
      email: { label: "Email", required: true, placeholder: "Enter your email" },
    },
    isOffline: false,
    autoRespondOffline: true,
    offlineAutoResponseMessage: "Thanks for your message! We'll get back to you soon.",
    chatIconUrl: "https://cdn.clikkle.com/images/crm/2025/clikkle%20crm.png",
    socketUrl: process.env.REACT_APP_SOCKET_URL || "http://localhost:9100",
    apiUrl: process.env.REACT_APP_API_URL || "http://localhost:9100",
    startConversationButtonText: "Start a Conversation",
  }

  // Update config merge to handle all possible configurations
  const config = {
    ...defaultConfig,
    ...(props.config || (typeof window !== "undefined" && window.$mychat && window.$mychat.config) || {}),
  }

  // State variables
  const [isOpen, setIsOpen] = useState(props.isOpen !== undefined ? props.isOpen : false)
  const [isOnline, setIsOnline] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [showPreChat, setShowPreChat] = useState(false)
  const [showSplash, setShowSplash] = useState(true)
  const [activeTab, setActiveTab] = useState("home")
  const [guestInfo, setGuestInfo] = useState({ name: "", email: "" })
  const [userDetails, setUserDetails] = useState({ name: "", email: "" })
  const [currentConversationId, setCurrentConversationId] = useState(null)
  const [widgetId, setWidgetId] = useState(props.config?.widgetId || null)
  const [initialMessageQueue, setInitialMessageQueue] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [messages, setMessages] = useState([])
  const [organizationId, setOrganizationId] = useState(props.config?.organizationId || null) // NEW INITIALIZATION
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [page, setPage] = useState(1)
  const [isGuestCurrentlyTyping, setIsGuestCurrentlyTyping] = useState(false) // New state for typing status
  const [isAgentOnline, setIsAgentOnline] = useState(false)
  const [conversationStatus, setConversationStatus] = useState(null)
  const [socket, setSocket] = useState(null)
  const [showChat, setShowChat] = useState(false)
  const [preChatError, setPreChatError] = useState("")
  const [inputValue, setInputValue] = useState("")
  const [showStartChatForm, setShowStartChatForm] = useState(false)
  const [startChatUserDetails, setStartChatUserDetails] = useState({ name: "", email: "" })
  const [startChatFormError, setStartChatFormError] = useState("")
  const heartbeatIntervalRef = useRef(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const socketRef = useRef(null) // Add a ref to hold the socket instance for cleanup
  const widgetIdUpdatedRef = useRef({}); // New ref to track if widgetId has been updated for an org
  const typingTimeoutRef = useRef(null) // New ref for typing timeout

  // Function to update the organization's widgetId on the backend
  const updateOrganizationWidgetId = useCallback(async () => {
    const currentAuthToken = getCookie("accessToken") || getCookie("widgetAccessToken");
    console.log(`ChatWidget: [updateOrganizationWidgetId Function] Called. organizationId: ${organizationId}, widgetId: ${widgetId}, authToken present: ${!!currentAuthToken}.`);
    console.log(`ChatWidget: [updateOrganizationWidgetId Function] Full token (if present, for debug): ${currentAuthToken}`);

    if (organizationId && widgetId && currentAuthToken && !widgetIdUpdatedRef.current[organizationId]) {
      try {
        console.log(`ChatWidget: [updateOrganizationWidgetId API Call] Attempting to send widgetId ${widgetId} to organization ${organizationId}. Full URL: ${API_BASE_URL}/api/organization/${organizationId}/widgetId`);
        const response = await fetch(`${API_BASE_URL}/api/organization/${organizationId}/widgetId`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentAuthToken}`,
          },
          body: JSON.stringify({ widgetId: widgetId }),
        });

        if (response.ok) {
          console.log(`ChatWidget: ✅ Successfully updated organization ${organizationId} with widgetId ${widgetId}.`);
          widgetIdUpdatedRef.current[organizationId] = true; // Mark as updated for this organization
        } else {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          console.error(`ChatWidget: ❌ Failed to update organization widgetId ${organizationId}. Status: ${response.status}. Error: ${errorData.message}`);
        }
      } catch (error) {
        console.error(`ChatWidget: 🚨 Error during widgetId update API call for organization ${organizationId}:`, error);
      }
    } else if (!organizationId) {
      console.log("ChatWidget: [updateOrganizationWidgetId Function] Skipping API call: organizationId is null.");
    } else if (!widgetId) {
      console.log("ChatWidget: [updateOrganizationWidgetId Function] Skipping API call: widgetId is null.");
    } else if (!currentAuthToken) {
      console.log("ChatWidget: [updateOrganizationWidgetId Function] Skipping API call: authToken is null.");
    } else if (widgetIdUpdatedRef.current[organizationId]) {
      console.log(`ChatWidget: [updateOrganizationWidgetId Function] Skipping API call: widgetId already updated for organization ${organizationId}.`);
    }
  }, [organizationId, widgetId]); // Dependencies: organizationId and widgetId (authToken will be gotten inside)

  // Emoji picker data
  const emojis = [
    "😀",
    "😃",
    "😄",
    "😁",
    "😆",
    "😅",
    "😂",
    "🤣",
    "😊",
    "😇",
    "🙂",
    "🙃",
    "😉",
    "😌",
    "😍",
    "🥰",
    "😘",
    "😗",
    "😙",
    "😚",
    "😋",
    "😛",
    "😝",
    "😜",
    "🤪",
    "🤨",
    "🧐",
    "🤓",
    "😎",
    "🤩",
    "🥳",
    "😏",
    "😒",
    "😞",
    "😔",
    "😟",
    "😕",
    "🙁",
    "☹️",
    "😣",
    "😖",
    "😫",
    "😩",
    "🥺",
    "😢",
    "😭",
    "😤",
    "😠",
    "😡",
    "🤬",
    "🤯",
    "😳",
    "🥵",
    "🥶",
    "😱",
    "😨",
    "😰",
    "😥",
    "😓",
    "🤗",
    "🤔",
    "🤭",
    "🤫",
    "🤥",
    "😶",
    "😐",
    "😑",
    "😬",
    "🙄",
    "😯",
    "😦",
    "😧",
    "😮",
    "😲",
    "🥱",
    "😴",
    "🤤",
    "😪",
    "😵",
    "🤐",
    "🥴",
    "🤢",
    "🤮",
    "🤧",
    "😷",
    "🤒",
    "🤕",
    "🤑",
    "🤠",
    "😈",
    "👍",
    "👎",
    "👌",
    "✌️",
    "🤞",
    "🤟",
    "🤘",
    "🤙",
    "👈",
    "👉",
    "👆",
    "🖕",
    "👇",
    "☝️",
    "👋",
    "🤚",
    "🖐️",
    "✋",
    "🖖",
    "👏",
    "🙌",
    "🤲",
    "🤝",
    "🙏",
    "✍️",
    "💪",
    "",
    "🦿",
    "🦵",
    "🦶",
    "❤️",
    "🧡",
    "💛",
    "💚",
    "💙",
    "💜",
    "🖤",
    "🤍",
    "🤎",
    "💔",
    "❣️",
    "💕",
    "💞",
    "💓",
    "💗",
    "💖",
    "💘",
    "💝",
    "💟",
    "☮️",
    "✝️",
    "☪️",
    "🕉️",
    "☸️",
    "✡️",
    "🔯",
    "🕎",
    "☯️",
    "☦️",
    "🛐",
    "⭐",
    "🌟",
    "✨",
    "⚡",
    "☄️",
    "💥",
    "🔥",
    "🌪️",
    "🌈",
    "☀️",
    "🌤️",
    "⛅",
    "🌦️",
    "🌧️",
    "⛈️",
    "🌩️",
    "🌨️",
    "❄️",
    "☃️",
    "⛄",
  ]

  const handleEmojiSelect = (emoji) => {
    setInputValue((prev) => prev + emoji)
    setShowEmojiPicker(false)
  }

  // Define authToken in the component scope
  const authToken = getCookie("widgetAccessToken") || getCookie("accessToken") || null

  // Refs
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const messagesContainerRef = useRef(null)

  // Typing Indicator State (Agent Receiving)
  const [isAgentTyping, setIsAgentTyping] = useState(false)
  const [agentTypingName, setAgentTypingName] = useState("Support")

  useEffect(() => {
    console.log("ChatWidget: isAgentTyping state changed to:", isAgentTyping)
  }, [isAgentTyping])

  // Agent Online Status State
  useEffect(() => {
    console.log("ChatWidget: isAgentOnline state changed to:", isAgentOnline)
  }, [isAgentOnline])

  useEffect(() => {
    console.log("ChatWidget: organizationId state has been updated to:", organizationId)
  }, [organizationId])

  const guestName = guestInfo.name || "Guest" // Use a consistent guest name for display

  // Add toggleOpen function
  const toggleOpen = () => {
    setIsOpen(!isOpen)
    if (isOpen) {
      // If closing
      setShowPreChat(false) // Reset pre-chat form
    } else {
      // If opening
      setUnreadCount(0) // Clear unread count when widget is opened
      if (!hasInteracted && (config.enablePreChatForm === undefined || config.enablePreChatForm)) {
        setShowPreChat(true)
      }
    }
    if (props.onToggle) props.onToggle(!isOpen)
  }

  // Define sendTypingEvent with useCallback
  const sendTypingEvent = useCallback(
    (isTyping) => {
      if (!socket) { console.warn("ChatWidget: sendTypingEvent - socket object not available..."); return; }
      if (!socket.connected) { console.warn("ChatWidget: sendTypingEvent - socket not connected..."); return; }
      if (!currentConversationId) { console.warn("ChatWidget: sendTypingEvent - currentConversationId not set..."); return; }
      if (!organizationId) { 
        console.warn("ChatWidget: sendTypingEvent - organizationId not set. Typing event NOT sent."); // Added log
        return; 
      }

      const eventName = isTyping ? "typing-start" : "typing-stop"
      const userName = guestInfo.name || localStorage.getItem("chikli_name") || "Guest"

      const typingData = {
        conversationId: currentConversationId,
        userType: "guest",
        userName: userName,
        organizationId: organizationId, // Will be null/undefined if not set
      }

      console.log(`ChatWidget: Preparing to emit ${eventName}. Data:`, JSON.stringify(typingData, null, 2))
      socket.emit(eventName, typingData)
    },
    [socket, currentConversationId, guestInfo.name, organizationId],
  ) // Dependencies for useCallback

  // Define loadMessages with useCallback
  const loadMessages = useCallback(async () => {
    if (isLoadingMessages || !hasMoreMessages) return

    setIsLoadingMessages(true)
    try {
      const effectiveApiUrl = config.apiUrl || process.env.REACT_APP_API_URL || "https://chikkle-testing.onrender.com"
      const authToken = getCookie("accessToken")

      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      }
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`
      }

      const response = await fetch(
        `${effectiveApiUrl}/api/messages/${localStorage.getItem("chikli_name") || "guest"}?page=${page}&limit=20`,
        { headers },
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get("content-type")
      if (!contentType || !contentType.includes("application/json")) {
        throw new TypeError("Oops, we haven't got JSON!")
      }

      const data = await response.json()

      if (!Array.isArray(data.messages)) {
        setHasMoreMessages(false)
        return
      }

      if (data.messages.length < 20) {
        setHasMoreMessages(false)
      }

      console.log("ChatWidget: 📥 Fetched messages:", data.messages)
      setMessages((prev) => [...data.messages, ...prev])
      setPage((prev) => prev + 1)
    } catch (error) {
      console.error("Error loading messages:", error)
      // If it's the first page and we get an error, show a welcome message
      if (page === 1) {
        setMessages([
          {
            content: config.greetingMessage || "Welcome! How can we help you today?",
            from: "agent",
            type: "text",
            timestamp: new Date(),
          },
        ])
      }
      setHasMoreMessages(false)
    } finally {
      setIsLoadingMessages(false)
    }
  }, [isLoadingMessages, hasMoreMessages, page, config.greetingMessage, config.apiUrl])

  // Add a check for widgetId in the component initialization
  useEffect(() => {
    if (!config.widgetId) {
      let storedId = localStorage.getItem("widgetId")
      if (!storedId) {
        storedId = `widget-${Math.random().toString(36).substr(2, 9)}`
        localStorage.setItem("widgetId", storedId)
      }
      setWidgetId(storedId); // Ensure widgetId state is updated
      config.widgetId = storedId // Also update config to reflect this
    }
    // Trigger the update function here as well, since widgetId might be available now
    updateOrganizationWidgetId();
  }, [config, updateOrganizationWidgetId]) // Added updateOrganizationWidgetId to dependencies

  // Fix useEffect dependencies
  useEffect(() => {
    console.log("💬 Chat Widget: Component initialized with config:", {
      widgetId: config.widgetId,
      name: config.name,
      position: config.position,
    })
  }, [config.widgetId, config.name, config.position])

  // Effect to initialize currentConversationId from localStorage when widgetId is available
  useEffect(() => {
    if (widgetId && currentConversationId === null) {
      // Only run if widgetId is set and currentConversationId is not yet initialized
      const storedConvId = localStorage.getItem(`chikli_conversationId_${widgetId}`)
      if (storedConvId) {
        console.log("ChatWidget: Initializing currentConversationId state from localStorage:", storedConvId)
        setCurrentConversationId(storedConvId)
      }
    }
  }, [widgetId, currentConversationId]) // currentConversationId is in deps to allow re-init if it's reset to null

  // Effect to update the organization's widgetId on the backend
  useEffect(() => {
    console.log(`ChatWidget: [updateOrganizationWidgetId Effect TRIGGERED] organizationId: ${organizationId}, widgetId: ${widgetId}.`);
    updateOrganizationWidgetId();
  }, [organizationId, widgetId, updateOrganizationWidgetId]); // Depend on organizationId, widgetId, and the memoized function

  // Socket initialization, event listeners, and heartbeat
  useEffect(() => {
    const initializeSocket = async () => {
      if (!widgetId || !organizationId) {
        console.log("[ChatWidget Socket DEBUG] Aborted socket initialization: Missing widgetId or organizationId.")
        if (socketRef.current) {
          socketRef.current.disconnect()
          setSocket(null)
        }
        return
      }

      const locationData = await getIPAddressAndLocation()
      const effectiveSocketUrl = config.socketUrl || SOCKET_URL

      const newSocketInstance = io(effectiveSocketUrl, {
        transports: ["websocket"],
        autoConnect: true,
        auth: {
          token: authToken,
          widgetId: widgetId,
          organizationId: organizationId,
          currentPage: window.location.href,
          language: navigator.language,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          referrer: document.referrer,
          browser: detectBrowser(),
          os: detectOS(),
          userAgent: navigator.userAgent,
          ipAddress: locationData.ip,
          location: {
            country: locationData.country,
            region: locationData.region,
            city: locationData.city,
            lat: locationData.lat,
            lon: locationData.lon,
          },
        },
      })
      socketRef.current = newSocketInstance // Store instance in ref for cleanup
      console.log(
        "🔌 [ChatWidget Socket DEBUG] New socket instance created, ID (pending connection):",
        newSocketInstance.id,
      )
      setSocket(newSocketInstance)

      const handleConnect = () => {
        console.log(`✅ [ChatWidget Socket DEBUG] Connected: ${newSocketInstance.id}`)
        setIsOnline(true)

        // The 'widget-connect' event is used to save visitor details on the backend.
        const connectData = {
          widgetId: widgetId,
          organizationId: organizationId,
          email: guestInfo.email || localStorage.getItem("chikli_email") || null,
          currentPage: window.location.href,
          userAgent: navigator.userAgent,
          language: navigator.language,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          referrer: document.referrer,
          browser: detectBrowser(),
          os: detectOS(),
          ipAddress: locationData.ip,
          country: locationData.country,
        }
        console.log("[ChatWidget Socket DEBUG] Emitting widget-connect with data:", connectData)
        newSocketInstance.emit("widget-connect", connectData)
      }

      const handleDisconnect = (reason) => {
        console.warn(`🔌 [ChatWidget Socket DEBUG] Disconnected: ${newSocketInstance.id}. Reason: ${reason}.`)
        setIsOnline(false)
        // TODO: Consider agent status change or heartbeat clear if needed
      }

      newSocketInstance.on("connect", handleConnect)
      newSocketInstance.on("disconnect", handleDisconnect)

      // Event Handlers
      const receiveMessageHandler = (message) => {
        // NEW: Plain function for testing
        console.log(
          `[ChatWidget ReceiveMsg DEBUG] receiveMessageHandler triggered. Current conversation ID: '${currentConversationId}'. Received message for conv ID: '${message?.conversationId}', content snippet: '${message?.content?.substring(0, 50)}...'`,
        )
        console.log("ChatWidget: 📩 Received message from server:", message) // Existing log

        if (message.senderType === "guest") {
          console.log("ChatWidget: Ignoring received message from guest to prevent duplication.")
          return
        }

        // Logic to add the message to the state
        setMessages((prevMessages) => {
          // Prevent adding duplicate messages if server echoes back a message with an _id we already have
          if (prevMessages.some((msg) => msg._id === message._id && message._id)) {
            console.log("ChatWidget: Message with server ID", message._id, "already exists. Not re-adding.")
            return prevMessages
          }

          const messageSender = message.senderType === "agent" || message.from === agentId ? "agent" : "user"
          const newMessage = {
            id: message._id || `server-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            _id: message._id,
            content: message.content || message.text || message.message || "",
            from: messageSender,
            timestamp: message.timestamp || new Date().toISOString(),
            read: message.read === undefined ? false : message.read,
            senderType: message.senderType,
            // Add conversationId to the message object in state if it's not there
            conversationId: message.conversationId || currentConversationId,
          }

          const currentMessages = Array.isArray(prevMessages) ? prevMessages : []
          const updatedMessages = [...currentMessages, newMessage]
          console.log("ChatWidget: 🗂️ Messages array updated with new message:", updatedMessages)
          return updatedMessages
        })

        // If the received message is part of a conversation that isn't the current one,
        // OR if no conversation is active, and this message starts one, update currentConversationId
        if (message.conversationId && message.conversationId !== currentConversationId) {
          console.log(
            `[ChatWidget ReceiveMsg DEBUG] Received message for a different conversation ('${message.conversationId}'). Updating currentConversationId from '${currentConversationId}'.`,
          )
          setCurrentConversationId(message.conversationId)
          if (widgetId) {
            localStorage.setItem(`chikli_conversationId_${widgetId}`, message.conversationId)
          }
        } else if (message.conversationId && !currentConversationId) {
          console.log(
            `[ChatWidget ReceiveMsg DEBUG] No active conversation. Setting currentConversationId from received message: '${message.conversationId}'.`,
          )
          setCurrentConversationId(message.conversationId)
          if (widgetId) {
            localStorage.setItem(`chikli_conversationId_${widgetId}`, message.conversationId)
          }
        }

        // Potentially update organizationId if it's missing and comes with the message
        if (message.organizationId && !organizationId) {
          console.log(
            `[ChatWidget ReceiveMsg DEBUG] Setting organizationId from received message: '${message.organizationId}'`,
          )
          setOrganizationId(message.organizationId)
        }
      } // NEW: End of plain function

      const newConversationHandler = (data) => {
        console.log("ChatWidget: 📨 New conversation event received:", data)
        if (data.conversationId) {
          if (!currentConversationId || currentConversationId !== data.conversationId) {
            console.log("ChatWidget: Updating currentConversationId from new-conversation event:", data.conversationId)
            setCurrentConversationId(data.conversationId)
            if (widgetId) {
              localStorage.setItem(`chikli_conversationId_${widgetId}`, data.conversationId)
            }
          }
          if (data.status) {
            setConversationStatus(data.status)
          }
        }
        if (data.organizationId && (!organizationId || organizationId !== data.organizationId)) {
          console.log("ChatWidget: Updating organizationId from new-conversation event:", data.organizationId)
          setOrganizationId(data.organizationId)
        }
        if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
          console.log("ChatWidget: Prepending messages from new-conversation event:", data.messages)
          setMessages((prev) => [...data.messages, ...prev])
        }
        if (data.widgetId && (!widgetId || widgetId !== data.widgetId)) {
          console.log("ChatWidget: Updating widgetId from new-conversation event:", data.widgetId)
          setWidgetId(data.widgetId)
        }
      }

      const typingStartHandler = (data) => {
        if (!data || !data.conversationId) {
          console.log("ChatWidget: Received typing-start with invalid data:", data)
          return
        }

        let conversationId = currentConversationId
        // If the widget doesn't have a conversation ID yet, adopt it from the event
        if (!conversationId && data.conversationId) {
          console.log(`ChatWidget: No active convId. Adopting from typing-start event: ${data.conversationId}`)
          setCurrentConversationId(data.conversationId)
          if (widgetId) {
            localStorage.setItem(`chikli_conversationId_${widgetId}`, data.conversationId)
          }
          conversationId = data.conversationId // Use it for the check below
        }

        console.log(
          `ChatWidget: Received typing-start. EventConvID: ${data.conversationId}, CurrentConvID (after potential update): ${conversationId}`,
        )
        if (data.conversationId === conversationId && data.userType === "agent") {
          console.log(`ChatWidget: Agent started typing. Data: ${JSON.stringify(data)}.`)
          setAgentTypingName(data.userName || "Support")
          setIsAgentTyping(true)
        }
      }

      const typingStopHandler = (data) => {
        if (!data || !data.conversationId) {
          console.log("ChatWidget: Received typing-stop with invalid data:", data)
          return
        }

        let conversationId = currentConversationId
        // If the widget doesn't have a conversation ID yet, adopt it from the event
        if (!conversationId && data.conversationId) {
          console.log(`ChatWidget: No active convId. Adopting from typing-stop event: ${data.conversationId}`)
          setCurrentConversationId(data.conversationId)
          if (widgetId) {
            localStorage.setItem(`chikli_conversationId_${widgetId}`, data.conversationId)
          }
          conversationId = data.conversationId // Use it for the check below
        }

        console.log(
          `ChatWidget: Received typing-stop. EventConvID: ${data.conversationId}, CurrentConvID (after potential update): ${conversationId}`,
        )
        if (data.conversationId === conversationId && data.userType === "agent") {
          console.log(`ChatWidget: Agent stopped typing. Data: ${JSON.stringify(data)}.`)
          setIsAgentTyping(false)
        }
      }

      const userStatusChangeHandler = (data) => {
        console.log("👤 User status changed:", data)
        if (data.userId === "support") {
          setIsAgentOnline(data.status === "online")
        }
      }

      const conversationUpdateHandler = (data) => {
        console.log("💬 Conversation update:", data)
        // Ensure 'conversationId' from props/state is used if it's the correct one to check against
        if (data.conversationId === currentConversationId) {
          // Assuming currentConversationId is the relevant one
          setConversationStatus(data.status)
        }
      }

      const agentStatusHandler = (data) => {
        console.log(
          "[ChatWidget agentStatusHandler DEBUG] Received agent-status event. Data:",
          data,
          "Current widget organizationId:",
          organizationId,
          "Current isAgentOnline:",
          isAgentOnline,
        )

        if (data.organizationId !== organizationId) {
          console.warn(
            "[ChatWidget agentStatusHandler DEBUG] Received agent-status for a different organization. Event OrgId:",
            data.organizationId,
            "Widget OrgId:",
            organizationId,
            "Ignoring.",
          )
          return
        }

        // Prefer supportStatus if available (indicates aggregate status for the org)
        if (data.hasOwnProperty("supportStatus")) {
          const newOnlineStatus = data.supportStatus === "online"
          if (isAgentOnline !== newOnlineStatus) {
            setIsAgentOnline(newOnlineStatus)
            console.log(
              `[ChatWidget agentStatusHandler DEBUG] Using supportStatus. Aggregate support status for org ${organizationId} changed to: ${data.supportStatus}. IsAgentOnline state set to: ${newOnlineStatus}`,
            )
          }
        } else if (data.hasOwnProperty("status")) {
          // Fallback to individual agent status
          const individualAgentOnline = data.status === "online"
          // If an agent comes online, the org is online.
          // If an agent goes offline, this is trickier. For now, if we receive an offline status,
          // and there's no overriding supportStatus, assume this means the service is offline.
          // This might need more sophisticated server-side logic to only send this if it's the *last* agent.
          if (isAgentOnline !== individualAgentOnline) {
            setIsAgentOnline(individualAgentOnline)
            console.log(
              `[ChatWidget agentStatusHandler DEBUG] Using individual status. Agent ${data.agentId || "N/A"} for org ${organizationId} reported status: ${data.status}. IsAgentOnline state set to: ${individualAgentOnline}`,
            )
          }
        } else {
          console.warn(
            "[ChatWidget agentStatusHandler DEBUG] Received agent-status event without supportStatus or status field. Data:",
            data,
          )
        }
      }

      const userOnlineHandler = (data) => {
        console.log("ChatWidget: User online:", data)
        if ((data.userRole === "agent" || data.userRole === "admin") && data.organizationId === organizationId) {
          if (!isAgentOnline) {
            setIsAgentOnline(true)
            console.log(
              `ChatWidget: Agent ${data.userId} from org ${organizationId} online. Setting widget agent status to online.`,
            )
          }
        }
      }

      const userOfflineHandler = (data) => {
        console.log("ChatWidget: User offline:", data)
        if ((data.userRole === "agent" || data.userRole === "admin") && data.organizationId === organizationId) {
          console.log(
            `ChatWidget: Agent ${data.userId} from org ${organizationId} offline. Widget status depends on aggregate.`,
          )
          // Do not set to false based on one agent going offline via this event.
        }
      }

      const agentOnlineHandler = (data) => {
        console.log("ChatWidget: Agent online event:", data)
        if (data.organizationId === organizationId && !isAgentOnline) {
          setIsAgentOnline(true)
          console.log(`ChatWidget: Agent ${data.agentId} online (via agent-online).`)
        }
      }

      const agentOfflineHandler = (data) => {
        console.log("ChatWidget: Agent offline event:", data)
        if (data.organizationId === organizationId && isAgentOnline) {
          // This event implies the backend has determined no relevant agents are left for this org.
          setIsAgentOnline(false)
          console.log(
            `ChatWidget: Agent ${data.agentId} offline (via agent-offline). Setting widget agent status to offline.`,
          )
        }
      }

      newSocketInstance.on("receive-message", receiveMessageHandler)
      newSocketInstance.on("new-conversation", newConversationHandler)
      newSocketInstance.on("typing-start", typingStartHandler)
      newSocketInstance.on("typing-stop", typingStopHandler)
      newSocketInstance.on("user-status-change", userStatusChangeHandler)
      newSocketInstance.on("conversation-update", conversationUpdateHandler)
      newSocketInstance.on("agent-status", agentStatusHandler)
      newSocketInstance.on("user-online", userOnlineHandler)
      newSocketInstance.on("user-offline", userOfflineHandler)
      newSocketInstance.on("agent-online", agentOnlineHandler)
      newSocketInstance.on("agent-offline", agentOfflineHandler)
    }

    initializeSocket()

    // Cleanup function
    return () => {
      const socketToClean = socketRef.current
      if (socketToClean) {
        console.log(`🧹 [ChatWidget Socket DEBUG Cleanup] Cleaning up for socket instance ID: ${socketToClean.id}.`)
        // Detach all listeners to prevent memory leaks
        socketToClean.off()
        if (socketToClean.connected) {
          console.log(`[ChatWidget Socket DEBUG Cleanup] Disconnecting socket instance ID: ${socketToClean.id}`)
          socketToClean.disconnect()
        }
        socketRef.current = null
      }
      setSocket(null)
      setIsOnline(false)
    }
  }, [widgetId, organizationId, authToken, guestInfo.name, isAgentOnline, config.socketUrl])

  // Remove old 'user-status-change' listener if it exists elsewhere or ensure it's not duplicated
  // newSocket.off('user-status-change'); // If it was added unconditionally before

  // Effect to join conversation room when socket is ready and currentConversationId is valid and changes
  useEffect(() => {
    console.log(
      `[ChatWidget RoomJoin DEBUG] useEffect triggered. Socket valid: ${!!socket}, Socket connected: ${socket?.connected}, currentConversationId: ${currentConversationId}, isOnline: ${isOnline}`,
    )
    if (socket && socket.connected && currentConversationId && isOnline) {
      // Added isOnline check
      console.log(
        `[ChatWidget RoomJoin DEBUG] ✅ Socket connected & online. Emitting join-conversation-room for currentConversationId: '${currentConversationId}'`,
      )
      // Use the same room name format as the agent dashboard
      socket.emit("join-conversation-room", currentConversationId)
    } else if (socket && (!socket.connected || !isOnline) && currentConversationId) {
      // Check if not connected OR not online
      console.warn(
        `[ChatWidget RoomJoin DEBUG] 🔌 Socket not connected or not fully online. Cannot join room for currentConversationId: '${currentConversationId}' yet. Connected: ${socket?.connected}, IsOnline: ${isOnline}`,
      )
    } else if (!socket) {
      // Simplified else-if for !socket
      console.warn(
        `[ChatWidget RoomJoin DEBUG] 🚫 Socket not available. currentConversationId is '${currentConversationId}'.`,
      )
    } else if (!currentConversationId) {
      console.log(`[ChatWidget RoomJoin DEBUG] 🤷 currentConversationId is null/empty. Not joining any room.`)
    }
  }, [socket, currentConversationId, isOnline]) // ADDED isOnline to dependency array

  // NEW: Effect to join organization-specific room for agent status updates
  useEffect(() => {
    if (socket && socket.connected && organizationId && isOnline) {
      console.log(
        `[ChatWidget OrgRoomJoin DEBUG] ✅ Socket connected & online. Emitting 'join-organization-room' for organizationId: '${organizationId}'`,
      )
      socket.emit("join-organization-room", organizationId)
    } else {
      let reason = ""
      if (!socket || !socket.connected || !isOnline) reason += "Socket not ready/online. "
      if (!organizationId) reason += "OrganizationId not set."
      if (reason) {
        console.log(`[ChatWidget OrgRoomJoin DEBUG] 🚫 Cannot join organization room yet. Reason: ${reason}`)
      }
    }
  }, [socket, organizationId, isOnline]) // Dependencies: socket, organizationId, isOnline

  // Effect to fetch organizationId if not already set (e.g., from props or localStorage)
  useEffect(() => {
    const fetchOrgId = async () => {
      // Condition: organizationId state is currently null (meaning it wasn't in props.config initially)
      // AND authToken is available to make the authenticated call.
      if (!organizationId && authToken) {
        // MODIFIED CONDITION
        console.log(
          "ChatWidget: organizationId is null (not in props) and authToken exists, attempting to fetch from API.",
        )
        try {
          const effectiveApiUrl = config.apiUrl || process.env.REACT_APP_API_URL || "http://localhost:9100";
          const response = await fetch(`${effectiveApiUrl}/api/organization`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          })
          if (!response.ok) {
            throw new Error(`Failed to fetch organization ID: ${response.status}`)
          }
          const orgData = await response.json()
          if (orgData && orgData.organization && orgData.organization._id) {
            console.log("ChatWidget: Fetched organizationId from API:", orgData.organization._id)
            setOrganizationId(orgData.organization._id)
          } else {
            console.warn("ChatWidget: organizationId not found in API response or response format unexpected.", orgData)
          }
        } catch (error) {
          console.error("ChatWidget: Error fetching organizationId:", error)
        }
      }
    }

    // If props.config.organizationId was provided, it's already used in useState.
    // If it was NOT provided (i.e., organizationId state is still null from useState initial value), then try to fetch.
    if (!props.config?.organizationId) {
      // MODIFIED LOGIC BLOCK
      fetchOrgId()
    }
    // Dependencies:
    // - authToken: if token appears later and orgId wasn't in props & is still null, fetch will run.
    // - props.config.organizationId: if this prop changes from undefined to defined, and orgId is still null, fetch won't run if new prop value makes it non-null.
    // - organizationId: if fetchOrgId sets it, this effect re-runs, but the '!organizationId' condition within fetchOrgId prevents re-fetch.
  }, [authToken, props.config?.organizationId, organizationId])

  useEffect(() => {
    console.log("💬 Chat Widget: Component initialized with config:", {
      widgetId: config.widgetId,
      name: config.name,
      position: config.position,
    })
  }, [config.widgetId, config.name, config.position])

  // Fix read receipts handling
  useEffect(() => {
    if (!socket) return

    const unreadMessages = messages.filter((msg) => msg.from === "agent" && !msg.read).map((msg) => msg.id)

    if (unreadMessages.length > 0) {
      // Placeholder for markMessagesAsRead function
    }
  }, [messages, socket])

  // useEffect for input field typing detection (ensure this uses sendTypingEvent)
  useEffect(() => {
    // const inputElement = document.querySelector(".chat-input-selector");
    const inputElement = inputRef.current; // Use the ref here

    const handleInput = (event) => {
      const value = event.target.value
      if (value.trim() === "") {
        sendTypingEvent(false)
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
          typingTimeoutRef.current = null
        }
      } else {
        sendTypingEvent(true)
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }
        typingTimeoutRef.current = setTimeout(() => {
          sendTypingEvent(false)
          typingTimeoutRef.current = null
        }, 1500)
      }
    }

    if (inputElement) {
      inputElement.addEventListener("input", handleInput)
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("input", handleInput)
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [socket, currentConversationId, sendTypingEvent])

  // Fix message loading
  useEffect(() => {
    if (isOpen && showChat) {
      loadMessages()
    }
  }, [isOpen, showChat, loadMessages])

  useEffect(() => {
    if (isOpen) {
      setShowSplash(true)
      setShowChat(false)
      setShowPreChat(false)
      setActiveTab("home")
      console.log("ChatWidget: 🚀 Widget opened, showing splash screen.")

      const splashTimer = setTimeout(() => {
        setShowSplash(false)
        console.log("ChatWidget: ✨ Splash screen hidden.")

        // Ensure currentConversationId is set *before* deciding on pre-chat/chat UI
        if (!currentConversationId && widgetId) { // Check state and ensure widgetId is available
          let convIdToSet = localStorage.getItem(`chikli_conversationId_${widgetId}`);
          if (!convIdToSet) {
            convIdToSet = `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem(`chikli_conversationId_${widgetId}`, convIdToSet);
            console.log("ChatWidget [isOpen effect]: Generated NEW currentConversationId and set in state:", convIdToSet);
            setCurrentConversationId(convIdToSet); // Update state
          } else {
            console.log("ChatWidget [isOpen effect]: Retrieved currentConversationId from localStorage and set in state:", convIdToSet);
            setCurrentConversationId(convIdToSet); // Update state, ensures consistency if other effect was racy
          }
        } else if (!widgetId) {
          console.warn("ChatWidget [isOpen effect]: widgetId not available when trying to set/get conversationId.");
        } else {
          console.log("ChatWidget [isOpen effect]: currentConversationId already existed or widgetId missing. Current ID:", currentConversationId);
        }

        // After splash, decide what to show based on config and localStorage
        if (config.requirePreChatForm) {
          const storedName = localStorage.getItem("chikli_name")
          const storedEmail = localStorage.getItem("chikli_email")
          if (storedName && storedEmail) {
            setUserDetails({ name: storedName, email: storedEmail })
            setShowPreChat(false)
            setActiveTab("chat") // Go to chat directly if details exist
            setShowChat(true)
            console.log("ChatWidget: 📝 Pre-chat details found in localStorage, skipping form, going to chat.")
          } else {
            setShowPreChat(true)
            setActiveTab("chat") // Or stay on a tab that shows the pre-chat form
            console.log("ChatWidget: 📝 Pre-chat required and no details in localStorage, showing pre-chat form.")
          }
        } else {
          // No pre-chat form required, go to chat directly or home initially
          setActiveTab("home") // Or 'chat' if you want to go directly to chat
          // Greeting message will be handled by activeTab effect or loadMessages
          console.log("ChatWidget: 📝 No pre-chat required.")
        }
      }, 3000) // Splash screen duration

      return () => clearTimeout(splashTimer)
    } else {
      // Reset states when widget is closed
      setShowSplash(false)
      setShowChat(false)
      setShowPreChat(false)
      setActiveTab("home")
      // setMessages([]); // Optionally clear messages when closed, or they will persist
      console.log("ChatWidget: 🚀 Widget closed.")
    }
  }, [isOpen, config.requirePreChatForm])

  useEffect(() => {
    // This effect handles showing the greeting message when chat becomes active
    // and no messages have been loaded or exist.
    if (activeTab === "chat" && showChat && messages.length === 0 && !isLoadingMessages && config.greetingMessage) {
      console.log(
        "ChatWidget: 🌟 Setting initial greeting message as chat tab is active, showChat is true, and messages array is empty.",
      )
      setMessages([
        {
          id: "greeting-message-" + Date.now(), // More unique ID
          content: config.greetingMessage,
          from: "agent",
          timestamp: new Date().toISOString(),
        },
      ])
    }
  }, [activeTab, showChat, messages, isLoadingMessages, config.greetingMessage])

  useEffect(() => {
    // This effect triggers loading messages when the chat tab is active and shown
    if (activeTab === "chat" && showChat) {
      console.log("ChatWidget: 💬 Chat tab is active and showChat is true, attempting to load messages.")
      loadMessages() // loadMessages will set its own greeting if it's page 1 and fetch fails
    }
  }, [activeTab, showChat, loadMessages])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages, isAgentTyping])

  useEffect(() => {
    if (activeTab === "chat") {
      const storedName = localStorage.getItem("chikli_name")
      const storedEmail = localStorage.getItem("chikli_email")
      if (storedName && storedEmail) {
        setUserDetails({ name: storedName, email: storedEmail })
        setShowStartChatForm(false)
        setShowChat(true)
      } else {
        setShowStartChatForm(true)
        setShowChat(false)
      }
    }
  }, [activeTab])

  const handlePreChatSubmit = (e) => {
    e.preventDefault()
    console.log("📝 Chat Widget: Pre-chat form submitted", userDetails)

    const errors = []
    if (config.preChatFormFields.name?.required && !userDetails.name.trim()) {
      errors.push(config.preChatFormFields.name.label || "Name")
    }
    if (config.preChatFormFields.email?.required && !userDetails.email.trim()) {
      errors.push(config.preChatFormFields.email.label || "Email")
    } else if (config.preChatFormFields.email?.required && !/\S+@\S+\.\S+/.test(userDetails.email)) {
      errors.push("a valid " + (config.preChatFormFields.email.label?.toLowerCase() || "email"))
    }
    if (errors.length > 0) {
      console.log("❌ Chat Widget: Pre-chat form validation failed", errors)
      setPreChatError(`Please enter ${errors.join(" and ")}.`)
      return
    }

    console.log("✅ Chat Widget: Pre-chat form validated successfully")
    setPreChatError("")
    setShowPreChat(false)
    if (config.greetingMessage) {
      setMessages([{ content: config.greetingMessage, from: "agent", type: "text", timestamp: new Date() }])
    }
  }

  // Update message sending to include read status
  const handleSendMessage = (e) => {
    e.preventDefault()
    const messageContent = inputValue.trim() // Use inputValue state

    if (!messageContent || !socket) {
      console.warn("ChatWidget: Message content empty or socket not ready (socket object missing).")
      return
    }

    if (!socket.connected) {
      console.warn("ChatWidget: Socket not connected. Cannot send message or join room.")
      // TODO: Maybe queue the message and try again once connected?
      return
    }

    let convIdToUse = currentConversationId

    if (!convIdToUse) {
      convIdToUse = `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      console.log(
        "[ChatWidget handleSendMessage DEBUG] No currentConversationId. Generated new one for initial message:",
        convIdToUse,
      )
      setCurrentConversationId(convIdToUse) // Set it in state
      // The useEffect for [socket, currentConversationId] should handle the join-room emit when currentConversationId updates and socket is connected.
      // However, to be safe, especially if this is the VERY first interaction, we can emit here if connected.
      // The server's join-conversation-room handler is idempotent for multiple joins to the same room.
      console.log(
        `[ChatWidget handleSendMessage DEBUG] Emitting join-conversation-room for new convId '${convIdToUse}' from handleSendMessage. Socket connected: ${socket.connected}`,
      )
      socket.emit("join-conversation-room", convIdToUse)
    }

    if (isGuestCurrentlyTyping) {
      clearTimeout(typingTimeoutRef.current)
      setIsGuestCurrentlyTyping(false)
      sendTypingEvent(false)
    }

    const guestNameToUse = guestInfo.name || localStorage.getItem("chikli_name") || "Guest"
    const timestamp = new Date().toISOString()
    const localMessageId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`
    const effectiveWidgetId = widgetId || config.widgetId
    const effectiveOrgId = organizationId || props.config?.organizationId

    if (!effectiveWidgetId) {
      console.error("ChatWidget: widgetId is missing. Cannot send message.")
      return
    }
    // Note: effectiveOrgId might be undefined if not set in config and widget is new.
    // Backend might need to handle this case by looking up orgId from widgetId.

    const messageDataToServer = {
      from: guestNameToUse,
      to: "agent-1",
      content: messageContent,
      timestamp: timestamp,
      widgetId: effectiveWidgetId,
      conversationId: convIdToUse,
      guestName: guestNameToUse,
      organizationId: effectiveOrgId, // This might be undefined, backend should be resilient
      senderType: "guest",
    }

    console.log("ChatWidget: 📨 Sending message to server:", JSON.stringify(messageDataToServer, null, 2))
    socket.emit("send-message", messageDataToServer)

    const messageDataForLocalDisplay = {
      id: localMessageId,
      content: messageContent,
      from: "user",
      userName: guestNameToUse,
      timestamp: timestamp,
      read: false,
      senderType: "guest",
    }

    setMessages((prevMessages) => [...prevMessages, messageDataForLocalDisplay])

    // Offline auto-response logic
    if (!isAgentOnline && config.autoRespondOffline && config.offlineAutoResponseMessage) {
      setTimeout(() => {
        const autoResponse = {
          id: `auto-response-${Date.now()}`,
          content: config.offlineAutoResponseMessage,
          from: "agent", // Display as if from an agent/system
          timestamp: new Date().toISOString(),
          senderType: "agent",
        }
        setMessages((prevMessages) => [...prevMessages, autoResponse])
      }, 500) // 500ms delay
    }

    setInputValue("")
    setHasInteracted(true)
    if (showPreChat) {
      setShowPreChat(false)
    }
  }

  // Handle scroll for pagination
  const handleScroll = () => {
    if (!messagesContainerRef.current) return

    const { scrollTop } = messagesContainerRef.current
    if (scrollTop === 0 && !isLoadingMessages && hasMoreMessages) {
      loadMessages()
    }
  }

  // Update messagesAreaStyle to include ref and scroll handler
  const messagesAreaStyle = {
    flex: 1,
    padding: "18px 10px 10px 10px",
    overflowY: "auto",
    background: config.widgetBackgroundColor || "linear-gradient(180deg, #f7fafd 0%, #ffffff 100%)",
    display: "flex",
    flexDirection: "column",
    gap: 1, // theme.spacing(1)
    scrollBehavior: "smooth",
    // ref and onScroll will be applied directly to the Box component
  }

  // Update renderMessages to show read status
  const renderMessages = () => {
    console.log("ChatWidget: 🖨️ renderMessages called. messages:", messages)
    return (
      <>
        {isLoadingMessages && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "10px",
              color: "#666",
            }}
          >
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography>Loading messages...</Typography>
          </Box>
        )}
        {messages.map((msg, idx) => {
          const key = msg.id || msg._id || msg.timestamp || idx
          const derivedFrom =
            msg.from === "agent" || msg.from === "agent-1" || msg.senderType === "agent" ? "agent" : "user"

          const content = msg.content || msg.text || msg.message || ""
          console.log("ChatWidget: 🖨️ Rendering message:", {
            key,
            derivedFrom,
            content,
            originalMsgFrom: msg.from,
            senderType: msg.senderType,
            msg,
          })

          return (
            <Box
            key={key}
            sx={{
              display: "flex",
              mb: 1.5,
              alignItems: "flex-end",
              justifyContent: derivedFrom === "user" ? "flex-end" : "flex-start",
            }}
          >
            {derivedFrom === "agent" && (
              <>
                <Avatar 
                  src={config.organizationLogo}
                  alt={config.name || "Agent"}
                  sx={{ width: 28, height: 28, mr: 1, alignSelf: "flex-end" }}
                  onError={(e) => {
                    console.log("ChatWidget: Error loading organization logo in message, falling back to default")
                    const name = config.name || "Support"
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=007bff&color=fff&size=64`
                  }}
                />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flexGrow: 1 }}>
                  <Paper
                    elevation={1}
                    sx={{
                      padding: "10px 15px",
                      borderRadius: "18px",
                      borderTopLeftRadius: "4px",
                      maxWidth: "70%",
                      wordBreak: "break-word",
                      backgroundColor: config.agentMessageBackgroundColor,
                      color: config.agentMessageTextColor,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography variant="body2">{content}</Typography>
                  </Paper>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      mt: 0.5,
                      ml: 1,
                      alignSelf: "flex-start",
                    }}
                  >
                    {formatTime(msg.timestamp)}
                  </Typography>
                </Box>
              </>
            )}
      
            {derivedFrom === "user" &&
              (() => {
                const userName = localStorage.getItem("chikli_name") || "Guest"
                const userInitials = userName.substring(0, 2).toUpperCase()
                const dynamicUserAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(userInitials)}&background=888&color=fff&size=64`
      
                return (
                  <>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexGrow: 1, mr: 1 }}>
                      <Paper
                        elevation={1}
                        sx={{
                          padding: "10px 15px",
                          borderRadius: "18px",
                          borderTopRightRadius: "4px",
                          maxWidth: "100%",
                          wordBreak: "break-word",
                          backgroundColor: config.userMessageBackgroundColor,
                          color: config.userMessageTextColor,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        }}
                      >
                        <Typography variant="body2">{content}</Typography>
                      </Paper>
                      <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, alignSelf: "flex-end" }}>
                        {formatTime(msg.timestamp)}
                      </Typography>
                    </Box>
                    <Avatar
                      src={dynamicUserAvatar}
                      alt={userInitials}
                      title={userName}
                      sx={{ width: 28, height: 28, alignSelf: "flex-end" }}
                    />
                  </>
                )
              })()}
          </Box>
          )
        })}
        {isAgentTyping && <WidgetTypingIndicator name={agentTypingName} />}
        <div ref={messagesEndRef} />
      </>
    )
  }

  // Update widgetStyles to use position from config
  const widgetStyles = {
    position: "fixed",
    ...(config.position === "bottom-left" 
      ? { left: 24 } 
      : config.position === "bottom-right" 
        ? { right: 24 }
        : { right: 24 }), // Default to bottom-right
    bottom: 24,
    zIndex: 9999,
    fontFamily: config.fontFamily,
    display: "flex",
    flexDirection: "column",
    alignItems: config.position === "bottom-left" ? "flex-start" : "flex-end",
    transition: "all 0.3s cubic-bezier(.4,2,.6,1)",
  }

  const chatBubbleStyle = {
    width:'52px',
    height: '62px',
    background: 'transparent',
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    position: "relative",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    outline: "none",
    overflow: "hidden",
    "&:hover": {
      transform: "scale(1.1) translateY(-2px)",
      boxShadow: "none",
      background: 'transparent',
      backgroundColor: 'transparent',
    },
    "&:active": {
      transform: "scale(1.05)",
    },
  }

  const glossyOverlaySx = { // Re-confirming removal/hiding
    display: 'none',
  };

  const chatWindowStyle = {
    width: "min(95vw, 380px)",
    height: "min(85vh, 580px)",
    background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    borderRadius: "24px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.8)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    marginTop: "12px",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    animation: isOpen ? "windowPop 0.4s cubic-bezier(0.4, 0, 0.2, 1)" : undefined,
    backdropFilter: "blur(20px)",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
      zIndex: 1,
    },
  }

  // Update headerStyle to use headerColor and headerTextColor
  const headerStyle = {
    padding: "16px 20px",
    background: `linear-gradient(135deg, ${config.headerColor} 0%, ${config.headerColor}dd 100%)`,
    color: config.headerTextColor,
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 56,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.2)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
    },
  }

  const agentInfoStyle = {
    display: "flex",
    alignItems: "center",
    gap: 1.25, // theme.spacing(1.25)
  }

  const avatarStyle = {
    // Applied to MUI Avatar component
    width: 40,
    height: 40,
    // marginRight: 1.25, // theme.spacing(1.25) // use gap in parent
    border: "2px solid rgba(255, 255, 255, 0.8)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff", // For Avatar, background is usually for placeholder
    objectFit: "cover", // For img inside Avatar if src is provided
  }

  const onlineDotStyle = (online) => ({
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: online ? "#4cd964" : "#ff3b30",
    border: "2px solid #fff",
    // marginLeft: -16, // Position relative to Avatar
    // marginTop: 24,
    boxShadow: "0 0 4px rgba(0,0,0,0.1)",
    position: "absolute", // This will need careful placement with MUI components
    // Example: bottom: 2, right: 2 on the Avatar's container or use Badge
    bottom: 3, // Adjust as needed
    right: 3, // Adjust as needed
  })

  const closeButtonStyle = {
    color: config.headerTextColor,
    marginLeft: 1,
    opacity: 0.9,
    transition: "all 0.2s ease",
    backgroundColor: "transparent",
    "&:hover": {
      opacity: 1,
      backgroundColor: "rgba(255,255,255,0.15)",
      transform: "scale(1.1)",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  }

  const inputAreaStyle = {
    padding: "10px 10px 0px 10px",
    borderTop: "1px solid rgba(226, 232, 240, 0.6)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    display: "flex",
    alignItems: "center",
    gap: 1,
    borderBottomLeftRadius: "24px",
    borderBottomRightRadius: "24px",
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "20px",
      right: "20px",
      height: "1px",
      background: "linear-gradient(90deg, transparent, rgba(226, 232, 240, 0.6), transparent)",
    },
  }

  const inputStyle = {
    flex: 1,
    minWidth: 0,
    borderRadius: "20px",
    fontSize: "15px",
    background: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.9)",
    color: "#1a202c",
    boxSizing: "border-box",
    ".MuiOutlinedInput-root": {
      borderRadius: "20px",
      backgroundColor: "#ffffff",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "& fieldset": {
        borderColor: "rgba(226, 232, 240, 0.8)",
        borderWidth: "1.5px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      },
      "&:hover fieldset": {
        borderColor: config.headerColor,
        boxShadow: `0 0 0 3px ${config.headerColor}20`,
      },
      "&.Mui-focused fieldset": {
        borderColor: config.headerColor,
        borderWidth: "2px",
        boxShadow: `0 0 0 4px ${config.headerColor}20`,
      },
    },
    ".MuiInputBase-input": {
      padding: "10px 0px",
      fontSize: "15px",
      lineHeight: "1.4",
      color: "#1a202c !important", // Force the text color to be visible
    },
  }

  const sendButtonStyle = {
    borderRadius: "20px",
    background: `#007bff`, // Hardcoded default blue
    color: `#ffffff`, // Hardcoded default white
    fontSize: "14px",
    fontWeight: 600,
    boxShadow: `0 4px 14px rgba(0,123,255,0.3), 0 2px 6px rgba(0,0,0,0.08)`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    flexShrink: 0,
    minWidth: 65,
    height: 42,
    padding: "10px 18px",
    textTransform: "none",
    border: "none",
    "&:hover": {
      background: `#007bff`, // Hardcoded default blue on hover
      boxShadow: `0 6px 20px rgba(0,123,255,0.4), 0 4px 12px rgba(0,0,0,0.12)`,
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
    "&:disabled": {
      background: "rgba(0,0,0,0.08)",
      color: "rgba(0,0,0,0.4)",
      boxShadow: "none",
    },
  }

  const preChatFormStyle = {
    // For Box acting as form
    padding: "32px 24px 24px 24px", // theme.spacing(4, 3, 3, 3)
    display: "flex",
    flexDirection: "column",
    gap: "22px", // theme.spacing(2.75)
    background: config.widgetBackgroundColor || "linear-gradient(180deg, #f7fafd 0%, #ffffff 100%)", // Use config or fallback
    minHeight: 320,
    justifyContent: "center",
  }

  const formFieldStyle = {
    // For FormControl
    display: "flex",
    flexDirection: "column",
    gap: 1, // theme.spacing(1)
  }

  const labelStyle = {
    // For FormLabel or Typography
    fontSize: "1rem",
    color: "#444",
    fontWeight: 600,
    marginLeft: "4px", // theme.spacing(0.5)
  }

  const formInputStyle = {
    // For TextField
    // padding: "14px 16px", // TextField handles padding
    borderRadius: "12px",
    // border: "1.5px solid rgba(224, 230, 237, 0.8)", // TextField variant="outlined"
    fontSize: "1rem",
    background: "#fff", // TextField background
    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
    transition: "all 0.2s ease",
    ".MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fff",
      "& fieldset": {
        borderColor: "rgba(224, 230, 237, 0.8)",
        borderWidth: "1.5px",
      },
    },
    ".MuiInputBase-input": {
      padding: "14px 16px",
    },
  }

  const errorStyle = {
    // For Typography
    color: "error.main", // Use theme error color
    fontSize: "0.95rem",
    marginTop: 0.625, // theme.spacing(0.625)
    fontWeight: 500,
  }

  const tabBarStyle = {
    borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    height: "48px",
    minHeight: "48px",
    "& .MuiTabs-indicator": {
      height: "2px", // Reduced from 3px
      borderRadius: "1px 1px 0 0",
    },
  }

  const tabStyle = (active) => ({
    flex: 1,
    fontWeight: active ? 600 : 500,
    fontSize: "14px",
    color: active ? config.headerColor : "#666",
    transition: "all 0.2s ease",
    textTransform: "none",
    minWidth: "auto",
    padding: "12px 16px",
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    paddingTop: "0px",
    justifyContent: "center",
    "&.Mui-selected": {
      color: config.headerColor,
      backgroundColor: "rgba(0, 123, 255, 0.05)",
    },
    "&:hover": {
      backgroundColor: active ? "rgba(0, 123, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
    },
    "& .MuiTab-iconWrapper": {
      marginBottom: "0px",
      marginRight: "6px",
    },
  })

  const homeContent = (
    <Box
      className="home-content"
      sx={{
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        overflowY: "auto",
        overflowX: "hidden",
        animation: "fadeIn 0.5s ease-out",
        flexGrow: 1,
      }}
    >
      {/* <Box
        sx={{
          width: 68, // Increased size to match original visual
          height: 68,
          borderRadius: "50%",
          border: "1px solid #4f8cff",
          boxShadow: "0 8px 32px 0 rgba(79,140,255,0.18), 0 2px 8px rgba(0,0,0,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "5px auto 24px auto",
          position: "relative",
          padding: "0", // Avatar will fill
          background: "#fff",
        }}
      >
        <Avatar
          src="https://cdn.clikkle.com/images/crm/2025/clikkle%20crm.png"
          alt="Chat Logo"
          sx={{ width: 50, height: 50 }}
        />
        <Box sx={glossyOverlaySx} />
      </Box> */}
      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontWeight: "600",
          color: "#333",
          margin: "0 0 12px 0",
          animation: "fadeInUp 0.5s ease-out 0.1s both",
        }}
      >
        Welcome to Clikkle Chat
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "#666",
          margin: "0 0 24px 0",
          lineHeight: "1.5",
          maxWidth: "280px",
          animation: "fadeInUp 0.5s ease-out 0.2s both",
        }}
      >
        We're here to help you with any questions or issues you might have.
      </Typography>
      <Button
        variant="contained"
        onClick={() => setActiveTab("chat")}
        sx={{
          background: `#007bff`, // Hardcoded default blue
          color: "white",
          borderRadius: "4px",
          padding: "10px 24px",
          fontSize: "14px",
          fontWeight: 600,
          marginBottom: "24px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
          transition: "all 0.2s ease",
          animation: "fadeInUp 0.5s ease-out 0.3s both",
          "&:hover": {
            transform: "translateY(-2px)",
            background: `#007bff`, // Hardcoded default blue
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          },
        }}
      >
        {config.startConversationButtonText}
      </Button>

      {[
        {
          icon: <AccessTimeIcon sx={{ color: "#1a73e8" }} />,
          title: "24/7 Support",
          text: "Our team is available around the clock to assist you.",
        },
        {
          icon: <EditIcon sx={{ color: "#1a73e8" }} />,
          title: "Effortless Typing",
          text: "Enjoy a smooth and easy typing experience, anytime.",
        },
        {
          icon: <SettingsIcon sx={{ color: "#1a73e8" }} />,
          title: "Customizable Chat",
          text: "Tailor your chat interface with dynamic options.",
        },
      ].map((item, index) => (
        <Paper
          key={index}
          elevation={1}
          sx={{
            borderRadius: "8px",
            padding: "16px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            maxWidth: "320px",
            width: "100%", // Make it responsive within its container
            background: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            animation: "fadeInUp 0.5s ease-out ${0.4 + index * 0.1}s both",
            marginTop: index > 0 ? "15px" : "0",
          }}
        >
          <Avatar
            sx={{
              width: "24px",
              height: "24px",
              background: "#f0f4f8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {item.icon}
          </Avatar>
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "600", color: "#333", margin: "0 0 4px 0" }}>
              {item.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", margin: "0", lineHeight: "1.4" }}>
              {item.text}
            </Typography>
          </Box>
        </Paper>
      ))}

      <Box
        sx={{
          marginTop: "24px",
          paddingTop: "16px",
          borderTop: "1px solid rgba(226, 232, 240, 0.8)",
          width: "100%",
          textAlign: "center",
          color: "#888",
          fontSize: "12px",
        }}
      >
        &copy; {new Date().getFullYear()} CLIKKLE. All rights reserved.
      </Box>
    </Box>
  )

  const handleStartChatFormSubmit = (e) => {
    e.preventDefault()
    const name = startChatUserDetails.name.trim()
    const email = startChatUserDetails.email.trim()
    let error = ""
    if (!name) error = "Please enter your name."
    else if (!email) error = "Please enter your email."
    else if (!/\S+@\S+\.\S+/.test(email)) error = "Please enter a valid email."
    if (error) {
      setStartChatFormError(error)
      return
    }
    // Save to localStorage
    localStorage.setItem("chikli_name", name)
    localStorage.setItem("chikli_email", email)
    setUserDetails({ name, email })
    setGuestInfo({ name, email }) // Also update guestInfo for immediate use
    setShowStartChatForm(false)
    setShowChat(true)
    // Potentially emit user details update if socket is connected
    if (socket && socket.connected) {
      const updateData = {
        widgetId: widgetId,
        organizationId: organizationId,
        email: email,
        name: name,
        // any other details
      }
      console.log("ChatWidget: Emitting update-visitor-details after start chat form", updateData)
      socket.emit("update-visitor-details", updateData)
    }
  }

  const startChatFormContent = (
    <Box component="form" onSubmit={handleStartChatFormSubmit} sx={preChatFormStyle} noValidate>
      <FormControl sx={formFieldStyle}>
        <FormLabel htmlFor="startchat-name" sx={labelStyle}>
          Name*
        </FormLabel>
        <TextField
          type="text"
          id="startchat-name"
          value={startChatUserDetails.name}
          onChange={(e) => setStartChatUserDetails({ ...startChatUserDetails, name: e.target.value })}
          placeholder="Enter your name"
          sx={formInputStyle}
          required
          variant="outlined"
          size="small"
        />
      </FormControl>
      <FormControl sx={formFieldStyle}>
        <FormLabel htmlFor="startchat-email" sx={labelStyle}>
          Email*
        </FormLabel>
        <TextField
          type="email"
          id="startchat-email"
          value={startChatUserDetails.email}
          onChange={(e) => setStartChatUserDetails({ ...startChatUserDetails, email: e.target.value })}
          placeholder="Enter your email"
          sx={formInputStyle}
          required
          variant="outlined"
          size="small"
        />
      </FormControl>
      {startChatFormError && (
        <Typography sx={errorStyle} role="alert">
          {startChatFormError}
        </Typography>
      )}
      <Button type="submit" sx={{ ...sendButtonStyle, mt: 1.5 }}>
        Start Chat
      </Button>
    </Box>
  )

  if (!config.widgetId) {
    console.warn(
      "ChatWidget: widgetId is not configured. This may cause issues with multiple widgets or backend integration.",
    )
  }

  useEffect(() => {
    console.log("ChatWidget: 🔄 messages state changed:", messages)
  }, [messages])

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest(".emoji-picker-container")) {
        setShowEmojiPicker(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showEmojiPicker])

  return (
    <>
      <GlobalStyles styles={animationStyles} />
      <Box sx={widgetStyles}>
        {!isOpen ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Fab sx={chatBubbleStyle} aria-label="Open chat" title="Open chat" onClick={toggleOpen}>
              {config.chatIconUrl ? (
                <Avatar
                  src={config.chatIconUrl}
                  alt="Chat Logo"
                  sx={{
                    width: '100%', // Make Avatar fill the Fab
                    height: '100%', // Make Avatar fill the Fab
                    objectFit: "contain",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden" // Ensure image is clipped within Avatar if needed
                  }}
                />
              ) : (
                <Typography variant="h6" sx={{ color: config.buttonTextColor || '#fff', fontSize: '16px' }}>
                  {config.welcomeButtonText || "💬"}
                </Typography>
              )}
            </Fab>
            {unreadCount > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '4px 8px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                {unreadCount}
              </Box>
            )}
          </Box>
        ) : (
          <Paper elevation={3} sx={chatWindowStyle} role="dialog" aria-labelledby="chatwidget-header">
            {showSplash && <SplashScreen config={config} />}
            {!showSplash && (
              <>
                <Box sx={headerStyle} id="chatwidget-header">
                  <Box sx={agentInfoStyle}>
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <Avatar 
                        src={config.organizationLogo}
                        alt={config.name || "Support"}
                        sx={avatarStyle}
                        onError={(e) => {
                          console.log("ChatWidget: Error loading organization logo in header, falling back to default")
                          const name = config.name || "Support"
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=007bff&color=fff&size=64`
                        }}
                      />
                      <Box sx={onlineDotStyle(isAgentOnline)} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: config.headerTextColor }}>
                        {config.name || "Support"}
                      </Typography>
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          color: config.headerTextColor,
                          opacity: 0.9,
                        }}
                      >
                        {isAgentOnline ? "Online" : "Offline"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: "12px" }}>
                    <IconButton sx={closeButtonStyle} aria-label="Minimize" size="small">
                      <MinimizeIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={toggleOpen} sx={closeButtonStyle} aria-label="Close" size="small">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Tabs
                  value={activeTab}
                  onChange={(event, newValue) => setActiveTab(newValue)}
                  sx={tabBarStyle}
                  indicatorColor="primary" // Or set to config.headerColor
                  TabIndicatorProps={{ style: { backgroundColor: config.headerColor } }}
                  variant="fullWidth"
                >
                  <Tab
                    icon={<HomeIcon />}
                    iconPosition="start"
                    label="Home"
                    value="home"
                    sx={tabStyle(activeTab === "home")}
                  />
                  <Tab
                    icon={<ChatIcon />}
                    iconPosition="start"
                    label="Chat"
                    value="chat"
                    sx={tabStyle(activeTab === "chat")}
                  />
                </Tabs>
                {/* Tab Content */}
                {activeTab === "home" && homeContent}
                {activeTab === "chat" && showStartChatForm && startChatFormContent}
                {activeTab === "chat" && !showStartChatForm && showChat && (
                  <>
                    <Box sx={messagesAreaStyle} ref={messagesContainerRef} onScroll={handleScroll}>
                      {renderMessages()}
                    </Box>
                    <Box component="form" onSubmit={handleSendMessage} sx={inputAreaStyle}>
                      <Box sx={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
                        <TextField
                          type="text"
                          value={inputValue}
                          onChange={(e) => {
                            setInputValue(e.target.value)
                            // Typing indicator logic
                            if (socket && currentConversationId) {
                              if (!isGuestCurrentlyTyping) {
                                sendTypingEvent(true) // Send typing-start
                                setIsGuestCurrentlyTyping(true)
                              }

                              if (typingTimeoutRef.current) {
                                clearTimeout(typingTimeoutRef.current)
                              }
                              typingTimeoutRef.current = setTimeout(() => {
                                sendTypingEvent(false) // Send typing-stop after 2 seconds of inactivity
                                setIsGuestCurrentlyTyping(false)
                                typingTimeoutRef.current = null
                              }, 2000) // Stop typing after 2s of inactivity
                            }
                          }}
                          onKeyDown={(e) =>
                            e.key === "Enter" && !e.shiftKey && !config.isOffline && handleSendMessage(e)
                          }
                          sx={{
                            ...inputStyle,
                            "& .MuiOutlinedInput-root": {
                              ...inputStyle[".MuiOutlinedInput-root"],
                              paddingRight: "45px", // Make room for emoji button
                            },
                          }}
                          placeholder={
                            !isAgentOnline && !config.autoRespondOffline // If offline AND no auto-response
                              ? "Chat is unavailable." // Show fixed message
                              : userDetails.name
                                ? `Message as ${userDetails.name}`
                                : config.chatInputPlaceholder
                          }
                          disabled={!socket || (!isAgentOnline && !config.autoRespondOffline)} // Adjusted disabled logic for input field
                          aria-label="Chat input"
                          className="chat-input-selector" // Keep class for now, though ref is primary
                          inputRef={inputRef} // Add the ref here
                          variant="outlined"
                          size="small"
                          multiline
                          maxRows={3}
                        />
                        <IconButton
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          sx={{
                            position: "absolute",
                            right: "8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: config.headerColor,
                            backgroundColor: "transparent",
                            zIndex: 1,
                            width: "32px",
                            height: "32px",
                            "&:hover": {
                              backgroundColor: "rgba(0, 123, 255, 0.08)",
                              transform: "translateY(-50%) scale(1.1)",
                            },
                            "&:active": {
                              transform: "translateY(-50%) scale(0.95)",
                            },
                          }}
                          size="small"
                        >
                          <EmojiEmotionsIcon fontSize="small" />
                        </IconButton>

                        {showEmojiPicker && (
                          <Paper
                            elevation={3}
                            sx={{
                              position: "absolute",
                              bottom: "100%",
                              right: 0,
                              marginBottom: "8px",
                              padding: "12px",
                              left: "10px",
                              borderRadius: "12px",
                              width: "260px",
                              maxHeight: "180px",
                              overflowY: "auto",
                              zIndex: 1000,
                              background: "#fff",
                              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                              // Ensure it doesn't overflow outside the chat container
                              transform: "translateX(-10px)", // Adjust horizontal position
                              // Custom scrollbar styling
                              "&::-webkit-scrollbar": {
                                width: "6px",
                              },
                              "&::-webkit-scrollbar-track": {
                                background: "rgba(0,0,0,0.05)",
                                borderRadius: "3px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                background: "rgba(0,0,0,0.2)",
                                borderRadius: "3px",
                                "&:hover": {
                                  background: "rgba(0,0,0,0.3)",
                                },
                              },
                              // Firefox scrollbar styling
                              scrollbarWidth: "thin",
                              scrollbarColor: "rgba(0,0,0,0.2) rgba(0,0,0,0.05)",
                              border: "1px solid rgba(226, 232, 240, 0.8)",
                            }}
                            className="emoji-picker-container"
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(8, 1fr)",
                                gap: "2px",
                              }}
                            >
                              {emojis.map((emoji, index) => (
                                <IconButton
                                  key={index}
                                  onClick={() => handleEmojiSelect(emoji)}
                                  sx={{
                                    fontSize: "16px",
                                    padding: "4px",
                                    minWidth: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    backgroundColor: "transparent",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "rgba(0, 123, 255, 0.08)",
                                      transform: "scale(1.15)",
                                    },
                                    "&:active": {
                                      transform: "scale(0.95)",
                                    },
                                  }}
                                >
                                  {emoji}
                                </IconButton>
                              ))}
                            </Box>
                          </Paper>
                        )}
                      </Box>
                      <Button
                        type="submit"
                        sx={sendButtonStyle}
                        disabled={!socket || (!isAgentOnline && !config.autoRespondOffline) || !inputValue.trim()} 
                        variant="contained"
                        endIcon={<SendIcon />}
                      >
                        {"Send"} {/* Hardcoded Send */}
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        padding: "4px 0",
                        textAlign: "center",
                        fontSize: "10px",
                        color: "#888",
                        marginTop:'10px',
                        background: "transparent",
                        borderBottomLeftRadius: "24px",
                        borderBottomRightRadius: "24px",
                      }}
                    >
                      Powered by{" "}
                      <Typography component="span" sx={{ color: "#007bff", fontWeight: 600, fontSize: "10px" }}>
                        Clikkle Chat
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>
    </>
  )
  
}

// Add this to the style tag at the bottom of the component
const animationStyles = `
  @media (max-width: 600px) {
    .chat-widget-advanced {
      width: 100vw !important;
      height: 100vh !important;
      border-radius: 0 !important;
      margin: 0 !important;
    }
  }
  @keyframes bubbleFloat {
    0% { box-shadow: 0 4px 16px rgba(0,0,0,0.18); transform: translateY(0); }
    100% { box-shadow: 0 8px 32px rgba(0,0,0,0.22); transform: translateY(-6px); }
  }
  @keyframes bubblePop {
    0% { transform: scale(0.8); }
    100% { transform: scale(1); }
  }
  @keyframes windowPop {
    0% { transform: scale(0.95); opacity: 0.7; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes scaleIn {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`

export default AdvancedChatWidget
